import { makeAutoObservable } from "mobx";
import { ICompareValueStore } from "./compare-value-factory";
import DateCompareValueField from "../../components/policy-editor/policy-eligibility-card/condition-group-card/condition-builder/condition-field/compare-value-type-fields/date-compare-value-field";
import { CONDITION_OPERATORS } from "../../policy-types";

/** ISO format: 2025-12-01T00:00:00.000Z */
export interface IDateCompareValue {
    from?: string;
    to?: string;
}

class DateCompareValueStore implements ICompareValueStore<IDateCompareValue> {
    private _model: IDateCompareValue = {} as IDateCompareValue;
    private _changes: Partial<IDateCompareValue> = {};
    private _uncommittedChanges: Partial<IDateCompareValue> = {};

    private _from?: string;
    private _to?: string;


    constructor(compareValue?: string) {
        makeAutoObservable(this);
        if (compareValue) {
            this._model = this._deserializeModel(compareValue);
        }
    }

    public onOperatorChanged(op: CONDITION_OPERATORS): void {
        if (op !== CONDITION_OPERATORS.OP_BETWEEN && op !== CONDITION_OPERATORS.OP_ON) {
            return;
        }

        this.setFrom(undefined);
        this.setTo(undefined);
    }

    public setChanges(changes?: string) {
        if (changes) {
            this._changes = this._deserializeModel(changes);
        }
    }

    get Component() {
        return DateCompareValueField;
    }

    /** Deserialize JSON string to IDateCompareValue model */
    private _deserializeModel(compareValue: string): IDateCompareValue {
        let model = {} as IDateCompareValue;
        try {
            model = JSON.parse(compareValue);

            /** Migrate old DD.MM.YYYY format to ISO UTC (backward compatibility) */
            if (model.from) {
                model.from = this._migrateToISO(model.from);
            }
            if (model.to) {
                model.to = this._migrateToISO(model.to);
            }
        }
        catch(e) {
            console.error(`invalid condition's compare value from DB: ${compareValue}`);
        }

        this._from = model.from;
        this._to = model.to;

        return model;
    }

    /** Migrate date format from DD.MM.YYYY to ISO UTC (backward compatibility) */
    private _migrateToISO(dateString: string): string | undefined {
        /** Check if already in ISO format */
        const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;
        if (isoRegex.test(dateString)) {
            return dateString;
        }

        /** Convert old DD.MM.YYYY format to ISO UTC */
        const ddmmyyyyRegex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
        const match = dateString.match(ddmmyyyyRegex);
        if (match) {
            const [, day, month, year] = match;

            /** Treat as UTC midnight */
            return `${year}-${month}-${day}T00:00:00.000Z`;
        }

        /** If format is unknown, return undefined to prevent crashes */
        /** Validator will catch this and show error */
        console.error(`Unable to parse date format: ${dateString}. Field will be treated as empty.`);
        return undefined;
    }

    /** Get from date in ISO format */
    public get from(): string|undefined {
        return this._from;
    }

    /** Get to date in ISO format */
    public get to(): string|undefined {
        return this._to;
    }

    /** Set from date in ISO format */
    public setFrom(isoString?: string) {
        this._from = isoString;
        this._uncommittedChanges.from = isoString;
    }

    /** Set to date in ISO format */
    public setTo(isoString?: string) {
        this._to = isoString;
        this._uncommittedChanges.to = isoString;
    }

    public commitChanges(): void {
        this._changes = {...this._changes, ...this._uncommittedChanges};
        this._clearUnneededChanges();
    }

    public discardUncommittedChanges(): void {
        /** Restore from and to to their previous values */
        const currentModel = { ...this._model, ...this._changes };
        this._from = currentModel.from;
        this._to = currentModel.to;
        this._uncommittedChanges = {};
    }

    public hasChanges(): boolean {
        return Object.values(this._changes).length > 0;
    }

    public discardChanges(): void {
        this._changes = {};
    }

    /** Serialize to JSON string for backend storage */
    public getUpdatedModel(): string|undefined {
        const serialized = JSON.stringify({ ...this._model, ...this._changes });
        if (serialized === JSON.stringify({})) {
            return undefined;
        }
        return serialized;
    }

    public getUncommittedChanges(): IDateCompareValue {
        return { ...this._model, ...this._changes, ...this._uncommittedChanges };
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IDateCompareValue;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }
}

export default DateCompareValueStore;
